/**
 * Single source of truth for the offsite briefing's audience & segmentation
 * options (FR-017e). MVR1 keeps these lists in the interface; migrating
 * their origin to the backend later is a source swap, not a screen
 * rewrite — every consumer must import from here, never declare inline.
 *
 * All display text (labels, descriptions) is resolved via i18n in the
 * consuming components (Interests.tsx, index.tsx) — never stored here as a
 * literal string. This file only holds the identifiers, icons and shape.
 */

export type AudienceRegion =
  | 'south'
  | 'southeast'
  | 'midwest'
  | 'northeast'
  | 'north'

export const AUDIENCE_REGION_OPTIONS: AudienceRegion[] = [
  'south',
  'southeast',
  'midwest',
  'northeast',
  'north'
]

export type AudienceAgeRange =
  | '18-24'
  | '25-34'
  | '35-44'
  | '45-54'
  | '55-64'
  | '65+'

export const AUDIENCE_AGE_RANGE_OPTIONS: AudienceAgeRange[] = [
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55-64',
  '65+'
]

export type AudienceSocialClass = 'a' | 'b' | 'c' | 'd_e'

export const AUDIENCE_SOCIAL_CLASS_OPTIONS: AudienceSocialClass[] = [
  'a',
  'b',
  'c',
  'd_e'
]

export type AudienceGender = 'male' | 'female'

export const AUDIENCE_GENDER_OPTIONS: AudienceGender[] = ['male', 'female']

export type InterestSubgroup = {
  id: string
}

export type InterestCategory = {
  id: string
  // Lucide icon name (kebab-case, same keys the prototype uses) — a string,
  // not a React component reference, so this stays portable if the list
  // moves server-side later. Resolved to a `react-icons/lu` component in
  // Interests.tsx via CATEGORY_ICON_BY_NAME.
  icon: string
  subgroups: InterestSubgroup[]
}

// 19 categories, 74 sub-groups total. `id` is the Publya `clusterId` —
// PINNED to specs/004-offsite-campaign-publya/publya_segments.json, the
// real Publya segment taxonomy (confirmed with the author 2026-08-28,
// superseding the prototype list this originally copied). Sub-group ids
// are slugs of the exact Publya `subItems` string for that cluster (e.g.
// "Produtores Rurais" -> "produtores_rurais") — do NOT rename, reorder, add
// or remove any category/sub-group here without updating that JSON and the
// i18n pt-BR text together: these values are meant to be dispatched to
// Publya as-is, and drifting from their contract silently would need a
// De-Para layer that doesn't exist and isn't planned. See spec.md —
// "Categorias de interesse" for the full rationale.
//
// Title, description and each sub-group's label live in i18n under
// `campaign.create.AudienceTargeting.interests.category.<id>.title` /
// `.description` / `.subgroup.<subgroupId>` — see i18n/*.json. pt-BR titles
// and sub-group labels come from publya_segments.json verbatim; English
// and Spanish keep this feature's own existing translations (not Publya's
// — Publya only publishes pt-BR). Descriptions in all 3 locales are still
// this feature's own text (unchanged, not sourced from Publya's
// `description` field, which reads differently — a separate open point).
export const INTEREST_CATEGORIES: InterestCategory[] = [
  {
    id: 'agro',
    icon: 'wheat',
    subgroups: [
      { id: 'agro_premium' },
      { id: 'agronegocio_empresarial' },
      { id: 'produtores_rurais' }
    ]
  },
  {
    id: 'automotivo',
    icon: 'car',
    subgroups: [
      { id: 'automoveis_premium' },
      { id: 'compra_de_veiculos' },
      { id: 'motos' },
      { id: 'servicos_automotivos' }
    ]
  },
  {
    id: 'b3',
    icon: 'line-chart',
    subgroups: [{ id: 'audiencia_premium_b3' }]
  },
  {
    id: 'beleza',
    icon: 'sparkles',
    subgroups: [
      { id: 'beleza_feminina' },
      { id: 'cabelos' },
      { id: 'maquiagem' }
    ]
  },
  {
    id: 'casa',
    icon: 'house',
    subgroups: [
      { id: 'casa' },
      { id: 'decoracao' },
      { id: 'eletrodomesticos' },
      { id: 'reforma_e_construcao' }
    ]
  },
  {
    id: 'educacao',
    icon: 'graduation-cap',
    subgroups: [
      { id: 'ensino_basico' },
      { id: 'graduacao' },
      { id: 'idiomas' },
      { id: 'pos_graduacao' }
    ]
  },
  {
    id: 'entretenimento',
    icon: 'clapperboard',
    subgroups: [
      { id: 'cinema' },
      { id: 'cultura' },
      { id: 'eventos' },
      { id: 'musica' },
      { id: 'streaming' }
    ]
  },
  {
    id: 'esportes',
    icon: 'trophy',
    subgroups: [
      { id: 'academia' },
      { id: 'apostas_esportivas' },
      { id: 'corrida' },
      { id: 'esportes_em_geral' },
      { id: 'fitness' },
      { id: 'futebol' }
    ]
  },
  {
    id: 'familia',
    icon: 'users',
    subgroups: [
      { id: 'familia' },
      { id: 'maternidade' },
      { id: 'paternidade' },
      { id: 'produtos_infantis' }
    ]
  },
  {
    id: 'financeiro',
    icon: 'credit-card',
    subgroups: [
      { id: 'bancos_e_servicos_financeiros' },
      { id: 'credito' },
      { id: 'financas_pessoais' },
      { id: 'investimentos' }
    ]
  },
  {
    id: 'imoveis',
    icon: 'key-round',
    subgroups: [
      { id: 'compra_de_primeiro_imovel' },
      { id: 'imoveis_de_luxo' },
      { id: 'imoveis_residenciais' },
      { id: 'investimento_imobiliario' }
    ]
  },
  {
    id: 'moda',
    icon: 'shirt',
    subgroups: [
      { id: 'moda_feminina' },
      { id: 'moda_masculina' },
      { id: 'moda_premium' },
      { id: 'tendencias_e_lifestyle' }
    ]
  },
  {
    id: 'negocios',
    icon: 'briefcase',
    subgroups: [
      { id: 'empreendedores' },
      { id: 'franquias' },
      { id: 'gestao_empresarial' },
      { id: 'pequenas_empresas' },
      { id: 'software_de_gestao' }
    ]
  },
  {
    id: 'pets',
    icon: 'paw-print',
    subgroups: [
      { id: 'donos_de_caes' },
      { id: 'donos_de_gatos' },
      { id: 'produtos_pet' },
      { id: 'saude_animal' }
    ]
  },
  {
    id: 'saude',
    icon: 'heart-pulse',
    subgroups: [
      { id: 'profissionais_da_saude' },
      { id: 'saude_familiar' },
      { id: 'saude_geral' }
    ]
  },
  {
    id: 'setor_publico',
    icon: 'landmark',
    subgroups: [
      { id: 'governos' },
      { id: 'instituicoes_publicas' },
      { id: 'servidores_publicos' }
    ]
  },
  {
    id: 'tecnologia',
    icon: 'monitor-smartphone',
    subgroups: [
      { id: 'geek_e_cultura_tech' },
      { id: 'inovacao_e_transformacao_digital' },
      { id: 'software' },
      { id: 'tecnologia_geral' },
      { id: 'telecomunicacoes' }
    ]
  },
  {
    id: 'turismo',
    icon: 'plane',
    subgroups: [
      { id: 'turismo_corporativo' },
      { id: 'turismo_de_luxo' },
      { id: 'viagens_internacionais' },
      { id: 'viagens_nacionais' }
    ]
  },
  {
    id: 'varejo',
    icon: 'shopping-bag',
    subgroups: [
      { id: 'black_friday' },
      { id: 'compradores_online' },
      { id: 'shopping' },
      { id: 'supermercado' }
    ]
  }
]
